import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  RefreshCw,
  ArrowRightLeft,
  Check,
  X,
  Users,
  Leaf,
  Loader2,
  ChevronRight,
  Minus,
  Plus,
  Beef,
  Carrot,
  Wheat,
  Droplets,
  AlertCircle
} from 'lucide-react';
import { haptic } from '@/utils/haptics';

/**
 * LightEdit - Quick meal plan adjustments without rebuilding
 */

// Component Swap Sheet - Swap individual meal components (protein/carb/veg/sauce)
export const ComponentSwapSheet = ({
  open,
  onOpenChange,
  mealId,
  mealTitle,
  dayIndex,
  onSwap
}) => {
  const { api } = useAuth();
  const [components, setComponents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [swapping, setSwapping] = useState(null);
  const [activeTab, setActiveTab] = useState('protein');

  const componentIcons = {
    protein: Beef,
    carb: Wheat,
    veg: Carrot,
    sauce: Droplets
  };

  const componentLabels = {
    protein: 'Protein',
    carb: 'Carb/Grain',
    veg: 'Vegetables',
    sauce: 'Sauce/Seasoning'
  };

  // Load components when sheet opens
  useEffect(() => {
    const loadComponents = async () => {
      if (!open || !mealId) return;
      
      setLoading(true);
      try {
        const response = await api.get(`/meal/components/${mealId}`);
        setComponents(response.data);
        
        // Set active tab to first available component
        if (response.data.components) {
          const firstComponent = Object.keys(response.data.components)[0];
          if (firstComponent) setActiveTab(firstComponent);
        }
      } catch (error) {
        console.error('Failed to load meal components:', error);
        toast.error('Failed to load meal components');
      } finally {
        setLoading(false);
      }
    };

    loadComponents();
  }, [open, mealId, api]);

  // Handle component swap
  const handleSwap = async (componentType, newIngredientId, newIngredientName) => {
    setSwapping(`${componentType}-${newIngredientId}`);
    haptic('medium');
    
    try {
      const response = await api.post('/meal/swap-component', {
        meal_id: mealId,
        component_type: componentType,
        new_ingredient_id: newIngredientId,
        day_index: dayIndex
      });
      
      haptic('success');
      toast.success(`Swapped to ${newIngredientName}`);
      
      // Update local state to show new component
      if (components && components.components) {
        const updatedComponents = { ...components };
        updatedComponents.components[componentType].current = {
          ingredient_id: newIngredientId,
          display_name: newIngredientName
        };
        setComponents(updatedComponents);
      }
      
      onSwap?.(response.data);
    } catch (error) {
      console.error('Failed to swap component:', error);
      toast.error(error.response?.data?.detail || 'Failed to swap component');
    } finally {
      setSwapping(null);
    }
  };

  const availableComponents = components?.components 
    ? Object.keys(components.components).filter(k => components.components[k]?.current)
    : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl pb-8 max-h-[85vh]">
        <SheetHeader className="text-left pb-4">
          <SheetTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-[#4A5D4A]" />
            Customize Components
          </SheetTitle>
          <SheetDescription>
            {mealTitle ? `Swap ingredients in "${mealTitle}"` : 'Swap individual components'}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-[#4A5D4A]" />
          </div>
        ) : !components?.has_component_model ? (
          <div className="text-center py-8">
            <AlertCircle className="h-10 w-10 text-[#5A6A5A] mx-auto mb-3" />
            <p className="text-[#5A6A5A]">
              {components?.message || 'Component swaps not available for this meal'}
            </p>
            {components?.swaps?.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-[#2D3A2D]">Quick swaps available:</p>
                {components.swaps.map((swap, idx) => (
                  <Badge key={idx} variant="secondary" className="mr-2">
                    {swap}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        ) : availableComponents.length > 0 ? (
          <div className="space-y-4">
            {/* Component Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${availableComponents.length}, 1fr)` }}>
                {availableComponents.map((compType) => {
                  const Icon = componentIcons[compType] || Carrot;
                  return (
                    <TabsTrigger 
                      key={compType} 
                      value={compType}
                      className="flex items-center gap-1 text-xs data-[state=active]:bg-[#4A5D4A] data-[state=active]:text-white"
                    >
                      <Icon className="h-3 w-3" />
                      {componentLabels[compType] || compType}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {availableComponents.map((compType) => {
                const comp = components.components[compType];
                const Icon = componentIcons[compType] || Carrot;
                
                return (
                  <TabsContent key={compType} value={compType} className="mt-4">
                    {/* Current Selection */}
                    <div className="mb-4 p-3 bg-[#4A5D4A]/10 rounded-lg border border-[#4A5D4A]/20">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="h-4 w-4 text-[#4A5D4A]" />
                        <span className="text-xs text-[#5A6A5A]">Current {componentLabels[compType]}</span>
                      </div>
                      <p className="font-semibold text-[#2D3A2D]">
                        {comp.current?.display_name || 'None selected'}
                      </p>
                      {comp.current?.qty_per_serving && (
                        <p className="text-xs text-[#5A6A5A] mt-1">
                          {comp.current.qty_per_serving} {comp.current.unit} per serving
                        </p>
                      )}
                    </div>

                    {/* Alternatives */}
                    <ScrollArea className="max-h-[40vh]">
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-[#2D3A2D] mb-2">
                          Swap to:
                        </p>
                        {comp.alternatives?.length > 0 ? (
                          comp.alternatives.map((alt) => (
                            <Card 
                              key={alt.ingredient_id}
                              className={`cursor-pointer transition-all hover:border-[#4A5D4A]/50 ${
                                swapping === `${compType}-${alt.ingredient_id}` ? 'border-[#4A5D4A]' : ''
                              }`}
                              onClick={() => handleSwap(compType, alt.ingredient_id, alt.display_name)}
                              data-testid={`swap-${compType}-${alt.ingredient_id}`}
                            >
                              <CardContent className="p-3 flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-sm text-[#2D3A2D]">
                                    {alt.display_name}
                                  </p>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {alt.diet_tags?.slice(0, 3).map((tag) => (
                                      <Badge 
                                        key={tag} 
                                        variant="outline" 
                                        className="text-[10px] px-1.5 py-0"
                                      >
                                        {tag}
                                      </Badge>
                                    ))}
                                    {alt.allergens?.length > 0 && (
                                      <Badge 
                                        variant="destructive" 
                                        className="text-[10px] px-1.5 py-0"
                                      >
                                        {alt.allergens.join(', ')}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                {swapping === `${compType}-${alt.ingredient_id}` ? (
                                  <Loader2 className="h-5 w-5 animate-spin text-[#4A5D4A]" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-[#5A6A5A]" />
                                )}
                              </CardContent>
                            </Card>
                          ))
                        ) : (
                          <p className="text-sm text-[#5A6A5A] text-center py-4">
                            No alternatives available
                          </p>
                        )}
                      </div>
                    </ScrollArea>
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>
        ) : (
          <div className="text-center py-8 text-[#5A6A5A]">
            No components available for this meal
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// Swap Meal Sheet Component
export const SwapMealSheet = ({ 
  open, 
  onOpenChange, 
  dayIndex, 
  currentMeal,
  onSwap 
}) => {
  const { api } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [swapping, setSwapping] = useState(null);

  // Load suggestions when sheet opens
  const loadSuggestions = async () => {
    if (!open || dayIndex === undefined) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/meal-plan/swap-suggestions/${dayIndex}`);
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Failed to load swap suggestions:', error);
      toast.error('Failed to load alternatives');
    } finally {
      setLoading(false);
    }
  };

  // Handle swap
  const handleSwap = async (newMealId, newMealTitle) => {
    setSwapping(newMealId);
    haptic('medium');
    
    try {
      await api.post(`/meal-plan/swap-meal?new_meal_id=${newMealId}`, {
        day_index: dayIndex
      });
      
      haptic('success');
      toast.success(`Swapped to ${newMealTitle}`);
      onSwap?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to swap meal:', error);
      toast.error('Failed to swap meal');
    } finally {
      setSwapping(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl pb-8 max-h-[85vh]" onOpenAutoFocus={loadSuggestions}>
        <SheetHeader className="text-left pb-4">
          <SheetTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Swap This Meal
          </SheetTitle>
          <SheetDescription>
            {currentMeal ? `Replace "${currentMeal}"` : 'Choose an alternative meal'}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : suggestions.length > 0 ? (
            <div className="space-y-2">
              {suggestions.map((meal) => (
                <Card 
                  key={meal.id}
                  className="cursor-pointer hover:border-primary/50 transition-all"
                  onClick={() => handleSwap(meal.id, meal.title)}
                  data-testid={`swap-option-${meal.id}`}
                >
                  <CardContent className="p-3 flex items-center gap-3">
                    {meal.image && (
                      <img 
                        src={meal.image} 
                        alt={meal.title}
                        className="h-14 w-14 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{meal.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">
                          {meal.prep_time} min
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {meal.difficulty}
                        </Badge>
                      </div>
                    </div>
                    {swapping === meal.id ? (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No alternative meals available
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

// Swap Ingredient Sheet Component
export const SwapIngredientSheet = ({
  open,
  onOpenChange,
  dayIndex,
  ingredientName,
  mealTitle,
  onSwap
}) => {
  const { api } = useAuth();
  const [alternatives, setAlternatives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [swapping, setSwapping] = useState(null);

  // Load alternatives when sheet opens
  const loadAlternatives = async () => {
    if (!open || !ingredientName) return;
    
    setLoading(true);
    try {
      const response = await api.get(
        `/meal-plan/ingredient-swaps/${dayIndex}/${encodeURIComponent(ingredientName)}`
      );
      setAlternatives(response.data.alternatives || []);
    } catch (error) {
      console.error('Failed to load ingredient alternatives:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle swap
  const handleSwap = async (newIngredient) => {
    setSwapping(newIngredient);
    haptic('medium');
    
    try {
      await api.post('/meal-plan/swap-ingredient', {
        day_index: dayIndex,
        meal_index: 0,
        original_ingredient: ingredientName,
        new_ingredient: newIngredient
      });
      
      haptic('success');
      toast.success(`Swapped to ${newIngredient}`);
      onSwap?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to swap ingredient:', error);
      toast.error('Failed to swap ingredient');
    } finally {
      setSwapping(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl pb-8" onOpenAutoFocus={loadAlternatives}>
        <SheetHeader className="text-left pb-4">
          <SheetTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Swap Ingredient
          </SheetTitle>
          <SheetDescription>
            Replace {ingredientName} in {mealTitle}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : alternatives.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {alternatives.map((alt) => (
              <Button
                key={alt}
                variant="outline"
                className="h-auto py-3 justify-start"
                onClick={() => handleSwap(alt)}
                disabled={swapping !== null}
                data-testid={`swap-ingredient-${alt}`}
              >
                {swapping === alt ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                )}
                {alt}
              </Button>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            No alternatives available for this ingredient
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// Servings Selector Component
export const ServingsSelector = ({ value, onChange, min = 1, max = 12 }) => {
  const handleChange = (delta) => {
    const newValue = Math.max(min, Math.min(max, value + delta));
    if (newValue !== value) {
      haptic('light');
      onChange(newValue);
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid="servings-selector">
      <Button
        variant="outline"
        size="icon"
        className="h-10 w-10 rounded-full"
        onClick={() => handleChange(-1)}
        disabled={value <= min}
      >
        <Minus className="h-4 w-4" />
      </Button>
      
      <div className="flex items-center gap-2 min-w-[80px] justify-center">
        <Users className="h-4 w-4 text-muted-foreground" />
        <span className="text-lg font-semibold">{value}</span>
      </div>
      
      <Button
        variant="outline"
        size="icon"
        className="h-10 w-10 rounded-full"
        onClick={() => handleChange(1)}
        disabled={value >= max}
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Have Ingredient Toggle
export const HaveIngredientToggle = ({ ingredientName, onToggle }) => {
  const { api } = useAuth();
  const [isHave, setIsHave] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleToggle = async () => {
    setLoading(true);
    haptic('light');
    
    try {
      if (isHave) {
        await api.delete(`/meal-plan/have-ingredient/${encodeURIComponent(ingredientName)}`);
        setIsHave(false);
        toast.success(`${ingredientName} added back to cart`);
      } else {
        await api.post('/meal-plan/have-ingredient', {
          ingredient_name: ingredientName
        });
        setIsHave(true);
        toast.success(`Marked as "already have it"`);
      }
      onToggle?.(!isHave);
    } catch (error) {
      console.error('Failed to toggle ingredient:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant={isHave ? "secondary" : "ghost"}
      size="sm"
      onClick={handleToggle}
      disabled={loading}
      className="h-8"
      data-testid={`have-ingredient-${ingredientName}`}
    >
      {loading ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : isHave ? (
        <>
          <Check className="h-3 w-3 mr-1" />
          Have it
        </>
      ) : (
        <>
          <X className="h-3 w-3 mr-1" />
          I have this
        </>
      )}
    </Button>
  );
};

// Dietary Filter Sheet
export const DietaryFilterSheet = ({ open, onOpenChange, onApply }) => {
  const { api } = useAuth();
  const [selectedDiet, setSelectedDiet] = useState('');
  const [autoSwap, setAutoSwap] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const diets = [
    { id: 'vegetarian', label: 'Vegetarian', icon: '🥬' },
    { id: 'gluten-free', label: 'Gluten-Free', icon: '🌾' },
    { id: 'low-carb', label: 'Low Carb', icon: '💪' },
    { id: 'dairy-free', label: 'Dairy-Free', icon: '🥛' },
  ];

  const handleApply = async () => {
    if (!selectedDiet) return;
    
    setLoading(true);
    haptic('medium');
    
    try {
      const response = await api.post('/meal-plan/apply-dietary-filter', {
        diet: selectedDiet,
        action: autoSwap ? 'swap_incompatible' : 'filter'
      });
      
      setResult(response.data);
      
      if (response.data.is_fully_compatible || response.data.swapped_meals?.length > 0) {
        haptic('success');
        toast.success(response.data.message);
        onApply?.();
        
        if (response.data.swapped_meals?.length > 0) {
          setTimeout(() => onOpenChange(false), 1500);
        }
      }
    } catch (error) {
      console.error('Failed to apply dietary filter:', error);
      toast.error('Failed to apply filter');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl pb-8">
        <SheetHeader className="text-left pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Leaf className="h-5 w-5" />
            Dietary Preferences
          </SheetTitle>
          <SheetDescription>
            Apply a dietary filter to your meal plan
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4">
          {/* Diet Selection */}
          <div className="grid grid-cols-2 gap-2">
            {diets.map((diet) => (
              <Button
                key={diet.id}
                variant={selectedDiet === diet.id ? "default" : "outline"}
                className="h-auto py-3 justify-start"
                onClick={() => {
                  haptic('light');
                  setSelectedDiet(diet.id);
                  setResult(null);
                }}
              >
                <span className="mr-2">{diet.icon}</span>
                {diet.label}
              </Button>
            ))}
          </div>

          {/* Auto-swap toggle */}
          <div className="flex items-center justify-between py-2">
            <Label htmlFor="auto-swap" className="text-sm">
              Auto-swap incompatible meals
            </Label>
            <Switch
              id="auto-swap"
              checked={autoSwap}
              onCheckedChange={setAutoSwap}
            />
          </div>

          {/* Result display */}
          {result && (
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-sm font-medium">{result.message}</p>
              {result.swapped_meals?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {result.swapped_meals.map((swap, idx) => (
                    <p key={idx} className="text-xs text-muted-foreground">
                      {swap.from} → {swap.to}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Apply button */}
          <Button
            className="w-full"
            onClick={handleApply}
            disabled={!selectedDiet || loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Apply Filter
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Quick Edit Bar Component (for meal planner)
export const QuickEditBar = ({ 
  servings, 
  onServingsChange,
  onDietaryClick 
}) => {
  const { api } = useAuth();
  const [updating, setUpdating] = useState(false);

  const handleServingsChange = async (newServings) => {
    setUpdating(true);
    try {
      await api.post('/meal-plan/update-servings', { servings: newServings });
      onServingsChange?.(newServings);
      toast.success(`Servings updated to ${newServings}`);
    } catch (error) {
      console.error('Failed to update servings:', error);
      toast.error('Failed to update servings');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div 
      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
      data-testid="quick-edit-bar"
    >
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Servings:</span>
          <ServingsSelector 
            value={servings} 
            onChange={handleServingsChange}
          />
        </div>
      </div>
      
      <Button
        variant="outline"
        size="sm"
        onClick={onDietaryClick}
        className="gap-2"
        data-testid="diet-filter-btn"
      >
        <Leaf className="h-4 w-4" />
        Diet
      </Button>
    </div>
  );
};

export default {
  ComponentSwapSheet,
  SwapMealSheet,
  SwapIngredientSheet,
  ServingsSelector,
  HaveIngredientToggle,
  DietaryFilterSheet,
  QuickEditBar
};
